import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Reaction Sport — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
const STAGE_SIZE = 280;
function StageBase({ children, onTap, ariaLabel, }) {
    if (onTap) {
        return (_jsx("button", { type: "button", onClick: onTap, "aria-label": ariaLabel, className: "flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-cyan-network", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: children }));
    }
    return (_jsx("div", { className: "flex items-center justify-center", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: children }));
}
function FixationCross() {
    return (_jsx(StageBase, { children: _jsx("span", { className: "select-none font-display text-[44px] text-cyan-surface", children: "+" }) }));
}
function WaitingSignal({ onFalseStart, label }) {
    return (_jsx(StageBase, { onTap: onFalseStart, ariaLabel: label, children: _jsx("span", { className: "select-none font-display text-[18px] text-cyan-text-secondary", children: label }) }));
}
function SignalSimple({ onTap, label }) {
    return (_jsx(StageBase, { onTap: onTap, ariaLabel: label, children: _jsxs("div", { className: "flex flex-col items-center gap-s3", children: [_jsx("span", { className: "block rounded-full", style: {
                        width: 140,
                        height: 140,
                        background: 'radial-gradient(circle at 30% 30%, #b9f0a3 0%, #4cd964 60%, #1f7a36 100%)',
                        boxShadow: '0 0 60px rgba(76, 217, 100, 0.45)',
                    }, "aria-hidden": true }), _jsx("span", { className: "select-none font-display text-[22px] font-medium text-cyan-surface", children: label })] }) }));
}
function SignalChoice({ signalColor, }) {
    const fill = signalColor === 'green' ? '#4cd964' : '#ff453a';
    const glow = signalColor === 'green' ? 'rgba(76, 217, 100, 0.45)' : 'rgba(255, 69, 58, 0.45)';
    return (_jsx(StageBase, { children: _jsx("span", { className: "block rounded-full", style: {
                width: 160,
                height: 160,
                background: `radial-gradient(circle at 30% 30%, #ffffff 0%, ${fill} 55%, #1a1a1a 110%)`,
                boxShadow: `0 0 60px ${glow}`,
            }, "aria-hidden": true }) }));
}
function FeedbackDisplay({ state, t, }) {
    const isCorrect = state.feedback === 'correct';
    let body;
    if (isCorrect) {
        const rt = state.lastRtMs ?? 0;
        body = t('reaction_sport.feedback.correct_with_rt', {
            rt: Math.round(rt),
            tier: t(tierKeyFor(rt)),
        });
    }
    else if (state.lastError === 'false_start') {
        body = t('reaction_sport.feedback.false_start');
    }
    else if (state.lastError === 'wrong_side') {
        body = t('reaction_sport.feedback.wrong_side');
    }
    else {
        body = t('reaction_sport.feedback.late');
    }
    return (_jsxs("div", { className: "flex flex-col items-center justify-center gap-s2", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: [_jsx("p", { className: [
                    'font-display text-[40px] font-medium',
                    isCorrect ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: isCorrect ? '✓' : '✗' }), _jsx("p", { className: "px-s4 text-center text-[12.5px] text-cyan-surface", children: body })] }));
}
function tierKeyFor(rtMs) {
    if (rtMs <= 180)
        return 'reaction_sport.tiers.elite';
    if (rtMs <= 250)
        return 'reaction_sport.tiers.pro';
    if (rtMs <= 400)
        return 'reaction_sport.tiers.active';
    return 'reaction_sport.tiers.apprentice';
}
function ChoicePicker({ onPick, disabled }) {
    const { t } = useTranslation('games');
    return (_jsxs("div", { className: "flex w-full max-w-[320px] items-stretch justify-center gap-s3", children: [_jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('green'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-core font-display text-[15px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network disabled:cursor-not-allowed disabled:opacity-50", children: t('reaction_sport.buttons.green') }), _jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('red'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-media font-display text-[15px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50", children: t('reaction_sport.buttons.red') })] }));
}
export function RSStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleTap = useCallback(() => adapter.handleTap(), [adapter]);
    const handlePick = useCallback((side) => adapter.handlePick(side), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const mode = stim?.mode ?? adapterState.variant.mode;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "relative", children: [adapterState.subPhase === 'fixation' && _jsx(FixationCross, {}), adapterState.subPhase === 'waiting_signal' && (_jsx(WaitingSignal, { onFalseStart: handleTap, label: t('reaction_sport.prompts.wait') })), adapterState.subPhase === 'signal' && stim && mode === 'simple' && (_jsx(SignalSimple, { onTap: handleTap, label: t('reaction_sport.prompts.tap_now') })), adapterState.subPhase === 'signal' && stim && mode === 'choice2' && (_jsx(SignalChoice, { signalColor: stim.signalColor })), adapterState.subPhase === 'feedback' && adapterState.feedback && (_jsx(FeedbackDisplay, { state: adapterState, t: t })), (adapterState.subPhase === 'iti' || adapterState.subPhase === 'idle') && (_jsx(StageBase, { children: null }))] }), mode === 'choice2' && (_jsx(ChoicePicker, { onPick: handlePick, disabled: adapterState.subPhase !== 'signal' })), _jsxs("div", { className: "text-[11px] text-cyan-text-secondary", children: [t('common:labels.current_flash'), " :", ' ', _jsxs("span", { className: "font-mono", children: [stim?.deadlineMs ?? '—', " ms"] }), ' · ', adapterState.variant.label] })] }));
}
function ageEquivalentKey(thresholdMs) {
    if (thresholdMs === null)
        return '';
    if (thresholdMs <= 220)
        return 'common:age.elite_active_25';
    if (thresholdMs <= 280)
        return 'common:age.active_35';
    if (thresholdMs <= 350)
        return 'common:age.around_45';
    if (thresholdMs <= 450)
        return 'common:age.around_55';
    return 'common:age.around_65_useful';
}
function recommendKey(threshold) {
    if (threshold !== null && threshold <= 250) {
        return 'reaction_sport.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold <= 400) {
        return 'reaction_sport.debrief.recommend_good';
    }
    return 'reaction_sport.debrief.recommend_keep';
}
export function RSDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0 ? 0 : Math.round((trialsCorrect / trialsCompleted) * 100);
    const fastestDeadline = useMemo(() => {
        if (staircase.trialLog.length === 0)
            return staircaseConfig.startValue;
        return Math.min(...staircase.trialLog.map((tt) => tt.value));
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : `${Math.round(threshold)} ms`;
    const replay = () => {
        window.location.reload();
    };
    const ageKey = ageEquivalentKey(threshold);
    const equivalentAge = ageKey ? t('common:age.equivalent_template', { label: t(ageKey) }) : '—';
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('reaction_sport.debrief.title'), scoreLabel: t('reaction_sport.debrief.score_label'), scoreValue: thresholdLabel, equivalentAge: equivalentAge, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('common:metrics.shortest'), value: `${Math.round(fastestDeadline)} ms` },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('reaction_sport.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map